/**
 * useBreakpoint — SSR-safe responsive breakpoint hook (issue #433).
 *
 * Returns a tagged union matching the CSS breakpoints we already
 * use in index.css:
 *
 *   mobile   < 768px
 *   tablet   768-1023px
 *   desktop  1024-1599px
 *   wide     >= 1600px
 *
 * The first render returns 'mobile' so the server-rendered HTML
 * (and React 19 SSR streaming) matches what the client paints on
 * mount. Once mounted, we listen to a `resize` event via
 * `window.matchMedia` and update the value in a useEffect — that
 * keeps hydration mismatch warnings away (the server never has
 * `window` so 'mobile' is always the safe first guess).
 *
 * Two separate media queries are used so we only need two
 * listeners regardless of how many breakpoints we expose:
 *   - (max-width: 767.98px)   → mobile
 *   - (min-width: 768px) and (max-width: 1023.98px) → tablet
 *   - (min-width: 1024px) and (max-width: 1599.98px) → desktop
 *   - (min-width: 1600px) → wide
 */
import { useEffect, useState } from 'react';

export type Breakpoint = 'mobile' | 'tablet' | 'desktop' | 'wide';

function detect(): Breakpoint {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'mobile';
  }
  if (window.matchMedia('(min-width: 1600px)').matches) return 'wide';
  if (window.matchMedia('(min-width: 1024px)').matches) return 'desktop';
  if (window.matchMedia('(min-width: 768px)').matches) return 'tablet';
  return 'mobile';
}

/**
 * Subscribes to the breakpoint media queries and re-renders on
 * change. Always returns 'mobile' on first render so SSR / first
 * paint doesn't flash a layout.
 */
export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>('mobile');

  useEffect(() => {
    // Set the real value on mount (skipping the SSR 'mobile'
    // default) and again on every media-query change.
    setBp(detect());
    const queries: Array<MediaQueryList> = [
      window.matchMedia('(min-width: 1600px)'),
      window.matchMedia('(min-width: 1024px)'),
      window.matchMedia('(min-width: 768px)'),
    ];
    function onChange(): void {
      setBp(detect());
    }
    for (const q of queries) {
      // Safari < 14 uses addListener; modern browsers use
      // addEventListener. Try both so the hook works on legacy
      // iPad WebViews that still ship with old WebKit.
      if (typeof q.addEventListener === 'function') {
        q.addEventListener('change', onChange);
      } else if (typeof q.addListener === 'function') {
        q.addListener(onChange);
      }
    }
    return () => {
      for (const q of queries) {
        if (typeof q.removeEventListener === 'function') {
          q.removeEventListener('change', onChange);
        } else if (typeof q.removeListener === 'function') {
          q.removeListener(onChange);
        }
      }
    };
  }, []);

  return bp;
}

/**
 * Pure helper that mirrors the hook's detect() logic. Exposed for
 * tests and for code paths that already know they're on the
 * client (e.g. a one-shot check inside an event handler).
 */
export function detectBreakpoint(): Breakpoint {
  return detect();
}
